//M10-16393 — Order quantity increase / decrease while the order is awaiting approval, for users with
// the order:edit-quantity RBAC right. Sent as ORDER_EDIT with edit_type "order_qty_edit", which the
// backend handles on its own (it does not approve the order) and keeps the counter PO in step.
import { useState } from "react";
import { Button, Input, Modal } from "src/components";
import { useToast } from "src/hooks/useToast";
import { PoPlaceOrder } from "src/services/order-api";

const ORDER_QTY_EDIT_TYPE = "order_qty_edit";

type QtyEditRow = {
  order_product_id: number;
  product_name: string;
  item_code: string;
  quantity: number;
  min_qty: number;
  scheme_value: number;
  price_identifier: number;
  new_qty: string;
};

interface OrderQtyEditModalProps {
  orderDetails: any;
  positionCode?: string;
  onClose: () => void;
  onSaved: (streamId?: string) => void;
}

const toRows = (orderDetails: any): QtyEditRow[] =>
  (orderDetails?.orderProducts || []).map((p: any) => ({
    order_product_id: p?.order_product_id,
    product_name: p?.product_name ?? "",
    item_code: p?.item_code ?? "",
    quantity: Number(p?.quantity ?? 0),
    min_qty: Number(p?.min_qty),
    scheme_value: Number(p?.free_quantity ?? 0),
    price_identifier: Number(p?.price_identifier ?? 0),
    new_qty: String(p?.quantity ?? ""),
  }));

// Free quantity at the new quantity, from the line's current buy:free ratio (scheme lines only)
const getDerivedFreeQty = (p: QtyEditRow): number => {
  const newQty = Number(p.new_qty);
  if (!Number.isFinite(newQty) || newQty <= 0) return 0;
  if (p.quantity <= 0 || p.scheme_value <= 0) return 0;
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(p.quantity, p.scheme_value);
  return Math.floor(newQty / (p.quantity / divisor)) * (p.scheme_value / divisor);
};

const getQtyError = (p: QtyEditRow): string => {
  const raw = (p.new_qty ?? "").toString().trim();
  if (raw === "") return "Quantity is required";
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return "Must be a whole number";
  if (n < 0) return "Must be non-negative";
  const minQty = Number.isFinite(p.min_qty) && p.min_qty > 0 ? p.min_qty : 0;
  if (n > 0 && minQty > 0 && n < minQty) return `Must be 0 or at least ${minQty}`;
  return "";
};

const OrderQtyEditModal = ({ orderDetails, positionCode, onClose, onSaved }: OrderQtyEditModalProps) => {
  const { toast } = useToast();
  const [rows, setRows] = useState<QtyEditRow[]>(() => toRows(orderDetails));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQtyChange = (idx: number, value: string) => {
    setRows((prev) => prev.map((p, i) => (i === idx ? { ...p, new_qty: value } : p)));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const invalid = rows.find((p) => getQtyError(p));
    if (invalid) {
      toast({ description: `${invalid.product_name}: ${getQtyError(invalid)}`, variant: "destructive" });
      return;
    }
    const changed = rows.filter((p) => Number(p.new_qty) !== p.quantity);
    if (changed.length === 0) {
      toast({ description: "No quantity changes to save", variant: "destructive" });
      return;
    }
    if (rows.every((p) => Number(p.new_qty) === 0)) {
      toast({ description: "At least one product must remain in the order", variant: "destructive" });
      return;
    }

    const payload = {
      data: {
        order_id_int: orderDetails?.order_id_int,
        edit_type: ORDER_QTY_EDIT_TYPE,
        actor_code: positionCode,
        orderProducts: changed.map((p) => ({
          order_product_id: p.order_product_id,
          quantity: Number(p.new_qty),
        })),
      },
      event_type: "ORDER_EDIT",
      primary_key: orderDetails?.order_id_int,
    };

    setIsSubmitting(true);
    try {
      // PoPlaceOrder resolves on 4xx/5xx too (validateStatus), so the status is checked here
      const res = await PoPlaceOrder(payload, orderDetails?.company_code);
      if (res?.status !== 200) throw new Error(res?.data?.message || "Failed to update order quantities");
      toast({ description: "Order quantities updated", variant: "default" });
      onSaved(res?.data?.data?.data?.eventStreamCreated?.streamId);
    } catch (error: any) {
      toast({
        description: error?.response?.data?.message || error?.message || "Failed to update order quantities",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal title="Edit Order Quantity" isHeader={true} isOpen={true} onCloseModal={onClose}>
      <div className="p-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500">No products available for this order.</p>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 pb-2 border-b">
              <div className="col-span-5">Product Name</div>
              <div className="col-span-2">Current Qty</div>
              <div className="col-span-2">Current Free Qty</div>
              <div className="col-span-3">New Qty</div>
            </div>
            {rows.map((p, idx) => (
              <div key={p.order_product_id} className="grid grid-cols-12 gap-2 items-center py-2 border-b border-gray-100">
                <div className="col-span-5 text-sm">
                  {p.product_name} ({p.item_code})
                </div>
                <div className="col-span-2 text-sm">{p.quantity}</div>
                <div className="col-span-2 text-sm">{p.scheme_value}</div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    value={p.new_qty}
                    onChange={(e: any) => handleQtyChange(idx, e.target.value)}
                    className="w-full"
                  />
                  {getQtyError(p) && <p className="text-red-500 text-xs mt-1">{getQtyError(p)}</p>}
                  {p.price_identifier === 2 && (
                    <p className="text-xs text-gray-600 mt-1">Free Qty: {getDerivedFreeQty(p)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500">The linked PO is updated to match. The order is not approved.</p>

        <footer className="flex gap-3 justify-end mt-2">
          <Button onClick={onClose} className="border">
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} loaderSize="small" className="text-white bg-[#586AF5]">
            Save
          </Button>
        </footer>
      </div>
    </Modal>
  );
};

export default OrderQtyEditModal;
